import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores';
import { useDatabase, useAppConfigList } from '../db';
import { useToast } from '../components/common/ToastProvider';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { now } from '../lib/uuid';
import { Settings as SettingsIcon, Folder, CreditCard, MapPin, Globe, Database, Download, Trash2, User, LogOut, Sparkles } from 'lucide-react';
import { CategoryManager } from '../components/settings/CategoryManager';
import { PaymentMethodManager } from '../components/settings/PaymentMethodManager';
import { BranchManager } from '../components/settings/BranchManager';
import { StaffRolesManager } from '../components/settings/StaffRolesManager';
import { ServicesManager } from '../components/settings/ServicesManager';
import { CustomSelect } from '../components/common/CustomSelect';
import { Button } from '../components/common/Button';

export function Settings() {
  const db = useDatabase();
  const user = useAuthStore((s) => s.user);
  const isOwner = useAuthStore((s) => s.isOwner);
  const signOut = useAuthStore((s) => s.signOut);
  const { config } = useAppConfigList();
  const toast = useToast();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  const [activeTab, setActiveTab] = useState<'general' | 'categories' | 'methods' | 'branches' | 'roles'>('general');

  // Settings State
  const [baseCurrency, setBaseCurrency] = useState('EUR');
  const [localCurrency, setLocalCurrency] = useState('VES');

  // Sync state with db configs
  useEffect(() => {
    if (config.length > 0) {
      const bCurr = config.find((c) => c.key === 'base_currency')?.value;
      if (bCurr) setBaseCurrency(bCurr);

      const lCurr = config.find((c) => c.key === 'local_currency')?.value;
      if (lCurr) setLocalCurrency(lCurr);
    }
  }, [config]);

  const handleSaveBaseCurrency = async (newVal: string) => {
    setBaseCurrency(newVal);
    if (!db || !isOwner) return;

    try {
      const username = user?.email || 'system';
      const existing = await db.app_config.findOne({ selector: { key: 'base_currency' } }).exec();

      if (existing) {
        await existing.patch({
          value: newVal,
          updated_at: now(),
          last_modified_by: username,
        });
      } else {
        await db.app_config.insert({
          id: 'config-currency-base',
          key: 'base_currency',
          value: newVal,
          owner_only: true,
          updated_at: now(),
          is_deleted: false,
          last_modified_by: username,
        });
      }
      toast.success('Configuración guardada: Moneda base actualizada con éxito.');
    } catch (err: any) {
      toast.error('Error al guardar configuración: ' + err.message);
    }
  };

  const handleExportBackup = async () => {
    try {
      const backup: Record<string, any[]> = {};
      const collections = [
        'transactions',
        'exchange_rates',
        'categories',
        'payment_methods',
        'branches',
        'app_config',
        'user_profiles',
        'services',
        'customers',
        'staff',
      ] as const;

      for (const col of collections) {
        const docs = await (db.collections as any)[col].find().exec();
        backup[col] = docs.map((d: any) => d.toJSON());
      }

      const jsonStr = JSON.stringify(backup, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `depilaway_backup_${dayjsFormat()}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Copia de seguridad exportada correctamente');
    } catch (err: any) {
      toast.error('Error al exportar base de datos: ' + err.message);
    }
  };

  const handleClearCache = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Limpiar Base de Datos Local',
      message: '¿Estás seguro de que deseas limpiar la base de datos local? Se borrarán todos los datos guardados en este dispositivo y la app se recargará.',
      onConfirm: async () => {
        try {
          await db.remove();
          window.location.reload();
        } catch (err: any) {
          toast.error('Error al limpiar base de datos: ' + err.message);
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const dayjsFormat = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">
          Panel de Control Administrativo
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Gestiona las entidades maestras, configuración regional y auditoría del sistema
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-2 border-b border-border overflow-x-auto hide-scrollbar pb-px">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
            activeTab === 'general'
              ? 'border-brand-orange text-brand-orange font-extrabold'
              : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-surface-secondary/50 rounded-t-lg'
          }`}
        >
          <SettingsIcon className="h-4 w-4" />
          General y Backups
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('categories')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
            activeTab === 'categories'
              ? 'border-brand-orange text-brand-orange font-extrabold'
              : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-surface-secondary/50 rounded-t-lg'
          }`}
        >
          <Folder className="h-4 w-4" />
          Categorías
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('methods')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
            activeTab === 'methods'
              ? 'border-brand-orange text-brand-orange font-extrabold'
              : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-surface-secondary/50 rounded-t-lg'
          }`}
        >
          <CreditCard className="h-4 w-4" />
          Métodos de Pago
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('branches')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
            activeTab === 'branches'
              ? 'border-brand-orange text-brand-orange font-extrabold'
              : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-surface-secondary/50 rounded-t-lg'
          }`}
        >
          <MapPin className="h-4 w-4" />
          Sucursales
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('roles')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
            activeTab === 'roles'
              ? 'border-brand-orange text-brand-orange font-extrabold'
              : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-surface-secondary/50 rounded-t-lg'
          }`}
        >
          <User className="h-4 w-4" />
          Roles de Personal
        </button>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'categories' && <CategoryManager />}
        {activeTab === 'methods' && <PaymentMethodManager />}
        {activeTab === 'branches' && <BranchManager />}
        {activeTab === 'roles' && <StaffRolesManager />}


        {activeTab === 'general' && (
          <div className="mx-auto max-w-2xl space-y-6">
            {/* Global Settings */}
            <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
              <div className="border-b border-border bg-surface-secondary px-6 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                  <Globe className="h-4 w-4 text-text-secondary" />
                  Configuración Regional
                </h2>
              </div>
              <div className="divide-y divide-border">
                {/* Base currency selection */}
                <div className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Moneda base</p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      Moneda contable para la consolidación de equivalencias
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isOwner && (
                      <span className="text-[10px] bg-warning/15 text-warning font-bold px-2 py-0.5 rounded-full">
                        Propietario únicamente
                      </span>
                    )}
                    <CustomSelect
                      disabled={!isOwner}
                      value={baseCurrency}
                      onChange={(val) => handleSaveBaseCurrency(val)}
                      options={[
                        { value: 'EUR', label: 'EUR — Euro' },
                        { value: 'USD', label: 'USD — Dólar' },
                      ]}
                      className="w-40"
                    />
                  </div>
                </div>

                {/* Local currency preview */}
                <div className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Moneda local</p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      Moneda nacional para transactions y estados de cuenta
                    </p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface-tertiary px-3 py-2 text-sm font-mono text-text-secondary">
                    {localCurrency} — Bolívar (VES)
                  </div>
                </div>
              </div>
            </div>

            {/* Data settings */}
            <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm animate-slide-up" style={{ animationDelay: '120ms' }}>
              <div className="border-b border-border bg-surface-secondary px-6 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                  <Database className="h-4 w-4 text-text-secondary" />
                  Datos y Copia de Seguridad
                </h2>
              </div>
              <div className="divide-y divide-border">
                {/* Export JSON database */}
                <div className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">
                      Respaldar Información
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      Descarga un archivo con toda tu información contable guardada
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Download}
                    onClick={handleExportBackup}
                  >
                    Exportar
                  </Button>
                </div>

                {/* Wipe Local cache / reset */}
                <div className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">
                      Restablecer Datos Locales
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      Limpia la información almacenada en este navegador y reinicia la aplicación
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={Trash2}
                    onClick={handleClearCache}
                  >
                    Limpiar
                  </Button>
                </div>
              </div>
            </div>

            {/* Account settings */}
            <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm animate-slide-up" style={{ animationDelay: '180ms' }}>
              <div className="border-b border-border bg-surface-secondary px-6 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                  <User className="h-4 w-4 text-text-secondary" />
                  Cuenta de Usuario
                </h2>
              </div>
              <div className="divide-y divide-border">
                {/* Email view */}
                <div className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">
                      Identificador de Usuario
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      Sesión activa utilizada para registrar modificaciones
                    </p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface-tertiary px-3 py-2 text-sm font-mono text-text-secondary">
                    {user?.email ?? 'Cargando...'}
                  </div>
                </div>

                {/* Sign out */}
                <div className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">
                      Cerrar Sesión
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      Salir del panel en este dispositivo
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={LogOut}
                    onClick={() => signOut()}
                  >
                    Salir
                  </Button>
                </div>
              </div>
            </div>

            {/* App version footer */}
            <div className="text-center text-[10px] text-text-tertiary pt-4 pb-12">
              Depilaway v1.0.0 — Sistema de control contable diseñado para trabajar sin interrupciones con o sin conexión
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Limpiar Datos"
      />
    </div>
  );
}
